import {
	Check,
	ChevronsDownUp,
	ChevronsUpDown,
	Copy,
	FileText,
	GitPullRequest,
	Inbox,
	Keyboard,
	LayoutList,
	Loader2,
	MessageCircle,
	Rows2,
	Rows4,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { withAvatarBase } from "../../gitlabHtml";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { hrefWithParam, useUrlParam } from "../../hooks/useUrlParam";
import { formatProjectName, safeHttpUrl } from "../../utils";
import { ApprovalStatus } from "../ApprovalStatus";
import { Avatar } from "../Avatar";
import { CopyLinkButton } from "../CopyLinkButton";
import { DateTime } from "../DateTime";
import { JiraBadge } from "../JiraBadge";
import { MRStatusIcons } from "../MRStatusIcons";
import { PipelineStatus } from "../PipelineStatus";
import { CollapseToggle, ResponseThread, previewOf } from "./responseThread";

const quoteMarkdown = (text) =>
	(text || "_No comment body provided._")
		.split("\n")
		.map((line) => `> ${line}`)
		.join("\n");

// Collapse keys include the latest activity time, so a card or thread the
// viewer collapsed reopens by itself when someone replies again. A card keys on
// `collapseAt` (its unfiltered latest activity, kept by filterByReviewer) so
// the reviewer filter does not change which cards read as collapsed.
const cardKey = (mr) =>
	`mr:${mr.id}@${mr.commentAttention.collapseAt ?? mr.commentAttention.latest.createdAt}`;
const threadKey = (thread) => `thread:${thread.id}@${thread.createdAt}`;
const MAX_COLLAPSED_KEYS = 300;

const useCollapsedState = () => {
	const [collapsed, setCollapsed] = useLocalStorage(
		"needsResponseCollapsed",
		{},
	);
	const setKeys = (keys, value) =>
		setCollapsed((previous) => {
			const next = { ...previous };
			for (const key of keys) {
				if (value) next[key] = true;
				else delete next[key];
			}
			// Bounded: drop the oldest entries (string keys keep insertion order).
			const all = Object.keys(next);
			for (const key of all.slice(0, all.length - MAX_COLLAPSED_KEYS)) {
				delete next[key];
			}
			return next;
		});
	return [collapsed, setKeys];
};

// Commenters across the inbox, busiest first, for the reviewer filter.
export const collectReviewers = (items) => {
	const reviewers = new Map();
	for (const mr of items) {
		for (const thread of mr.commentAttention.threads) {
			const { username } = thread.author;
			if (!reviewers.has(username)) {
				reviewers.set(username, { author: thread.author, count: 0 });
			}
			reviewers.get(username).count++;
		}
	}
	return [...reviewers.values()].sort(
		(a, b) =>
			b.count - a.count ||
			(a.author.name || "").localeCompare(b.author.name || ""),
	);
};

// Narrows each MR to one reviewer's threads, dropping MRs left with none.
// Threads stay newest first, so the first one is the new `latest`.
export const filterByReviewer = (items, username) =>
	username
		? items.flatMap((mr) => {
				const threads = mr.commentAttention.threads.filter(
					(thread) => thread.author.username === username,
				);
				return threads.length
					? [
							{
								...mr,
								commentAttention: {
									count: threads.length,
									latest: threads[0],
									collapseAt:
										mr.commentAttention.collapseAt ??
										mr.commentAttention.latest.createdAt,
									threads,
								},
							},
						]
					: [];
			})
		: items;

const buildAgentMarkdown = (items) => {
	const threadCount = items.reduce(
		(total, mr) => total + mr.commentAttention.count,
		0,
	);
	const sections = items.map((mr) => {
		const threads = mr.commentAttention.threads.map((thread, index) => {
			const authorName = thread.author.name || thread.author.username;
			return `### Pending thread ${index + 1}

- Commenter: ${authorName} (@${thread.author.username})
- Commented at: ${thread.createdAt}
- Comment URL: ${thread.url || "Unavailable"}
${thread.code ? `- Code: ${thread.code.path}:${thread.code.line} at ${thread.code.ref}\n` : ""}- Resolvable thread: ${thread.resolvable ? "Yes" : "No"}

${quoteMarkdown(thread.body)}`;
		});

		return `## !${mr.iid}: ${mr.title}

- Project: ${mr.projectPath}
- Merge request: ${mr.webUrl}
- Author: ${mr.author.name} (@${mr.author.username})
- Last updated: ${mr.updatedAt}
- Pending human threads: ${mr.commentAttention.count}

${threads.join("\n\n")}`;
	});

	return `# Pending merge request responses

Review every pending human comment below. For each thread, inspect the merge request and relevant code, determine whether a code change or written response is needed, then draft a concise thread reply. Do not mark a thread resolved until its concern has been addressed.

Summary: ${items.length} open merge requests and ${threadCount} pending human threads.

${sections.join("\n\n---\n\n")}`;
};

const KEYBOARD_SHORTCUTS = [
	["j / k", "Next / previous thread"],
	["x", "Collapse or expand thread"],
	["Shift x", "Collapse or expand merge request"],
	["o", "Open in GitLab"],
	["?", "Show or hide shortcuts"],
];

const isTypingTarget = (target) =>
	target instanceof HTMLElement &&
	(target.isContentEditable ||
		["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

// j/k/x/o navigation over the [data-nav-item] elements in `containerRef`,
// in DOM order, so it follows whatever is currently expanded and filtered.
const useInboxKeyboard = (containerRef, enabled, onToggleHelp) => {
	useEffect(() => {
		if (!enabled) return;
		const focusItem = (element) => {
			if (!element) return;
			element.focus({ preventScroll: true });
			element.scrollIntoView({ block: "nearest", behavior: "smooth" });
		};
		const onKeyDown = (event) => {
			if (event.metaKey || event.ctrlKey || event.altKey) return;
			if (isTypingTarget(event.target)) return;
			const container = containerRef.current;
			if (!container) return;
			const items = [...container.querySelectorAll("[data-nav-item]")];
			const current = document.activeElement?.closest?.("[data-nav-item]");
			const index = items.indexOf(current);
			const card = current?.closest("[data-card]");

			switch (event.key) {
				case "j":
					focusItem(items[index + 1] ?? items[index === -1 ? 0 : index]);
					break;
				case "k":
					focusItem(items[Math.max(0, index - 1)]);
					break;
				case "x":
					(current?.matches("[data-nav-toggle]")
						? current
						: current?.querySelector("[data-nav-toggle]")
					)?.click();
					break;
				case "X": {
					if (!card) return;
					card.querySelector("[data-card-toggle] button")?.click();
					// The focused thread may have just unmounted; land on the card.
					requestAnimationFrame(() =>
						focusItem(card.querySelector("[data-nav-item]")),
					);
					break;
				}
				case "o":
					current?.querySelector("[data-nav-open]")?.click();
					break;
				case "?":
					onToggleHelp();
					break;
				default:
					return;
			}
			event.preventDefault();
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [containerRef, enabled, onToggleHelp]);
};

const ResponseCard = ({ mr, collapsed, setKeys, compact }) => {
	const { count, threads, latest } = mr.commentAttention;
	const mrUrl = safeHttpUrl(mr.webUrl);
	const projectUrl = safeHttpUrl(mr.webUrl?.split("/-/")[0]);
	const isCardCollapsed = !!collapsed[cardKey(mr)];
	const threadsId = `threads-${mr.id}`;
	const latestAuthor = latest.author.name || latest.author.username;
	const project = mr.targetProject?.fullPath || mr.project?.fullPath;
	const toggleCard = () => setKeys([cardKey(mr)], !isCardCollapsed);
	const threadLabel = `${count} ${count === 1 ? "thread" : "threads"}`;

	return (
		<article
			data-card
			className="min-w-0 overflow-hidden rounded-2xl bg-[var(--response-card)] shadow-[0_1px_2px_rgb(0_0_0/0.08),0_8px_28px_rgb(0_0_0/0.08)] ring-1 ring-[var(--response-ring)]"
		>
			<header className={compact ? "px-4 py-2" : "px-5 py-3.5 sm:px-6"}>
				<div
					className={`flex min-w-0 flex-col lg:flex-row lg:justify-between ${
						compact ? "gap-2 lg:items-center" : "gap-4 lg:items-start"
					}`}
				>
					<div className="grid min-w-0 grid-cols-[auto_auto_minmax(0,1fr)] items-start gap-x-3">
						<span className="row-start-1 flex h-7 items-center">
							<span data-card-toggle className="contents">
								<CollapseToggle
									collapsed={isCardCollapsed}
									onToggle={toggleCard}
									label={`!${mr.iid}`}
									controls={threadsId}
									className="-ml-2"
								/>
							</span>
						</span>
						<span className="row-start-1 flex h-7 items-center">
							<Avatar
								src={mr.author.avatarUrl}
								alt={mr.author.name}
								size={compact ? "md" : "lg"}
								className="shrink-0 ring-1 ring-black/10 dark:ring-white/10"
							/>
						</span>
						<div className="col-start-3 min-w-0">
							<div className="flex min-h-7 min-w-0 items-center gap-2">
								{!compact && (
									<span className="flex shrink-0 items-center text-[var(--response-subtle-text)]">
										<GitPullRequest className="h-4 w-4" />
									</span>
								)}
								{mrUrl ? (
									<a
										href={mrUrl}
										target="_blank"
										rel="noopener noreferrer"
										className={`min-w-0 font-semibold leading-snug text-[var(--color-text-primary)] hover:text-[var(--color-link)] [text-wrap:balance] ${
											compact ? "truncate text-sm" : "text-lg"
										}`}
									>
										<span className="text-[var(--response-subtle-text)]">
											!{mr.iid}
										</span>{" "}
										{mr.title}
									</a>
								) : (
									<h2 className="text-lg font-semibold leading-snug [text-wrap:balance]">
										!{mr.iid} {mr.title}
									</h2>
								)}
							</div>
							<div
								className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-[var(--response-muted-text)] ${
									compact ? "text-xs" : "mt-1.5 text-sm"
								}`}
							>
								{projectUrl ? (
									<a
										href={projectUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="font-medium hover:text-[var(--color-link)]"
									>
										{formatProjectName(mr.projectPath)}
									</a>
								) : (
									<span>{formatProjectName(mr.projectPath)}</span>
								)}
								<JiraBadge mr={mr} />
								{!compact && (
									<>
										<span aria-hidden="true">·</span>
										<span>{mr.author.name}</span>
									</>
								)}
								<span aria-hidden="true">·</span>
								<span className="tabular-nums">
									Updated <DateTime date={mr.updatedAt} />
								</span>
								{compact && (
									<>
										<span aria-hidden="true">·</span>
										<span className="font-medium tabular-nums text-[var(--status-attention-text)]">
											{threadLabel}
										</span>
									</>
								)}
							</div>
						</div>
					</div>

					{compact ? (
						// Compact keeps only the signals that change what you do next.
						<div className="flex shrink-0 items-center gap-1 self-end lg:self-center">
							<span className="flex h-7 w-7 items-center justify-center">
								<PipelineStatus pipeline={mr.headPipeline} />
							</span>
							<ApprovalStatus
								aiReviewMissing={mr.aiReviewMissing}
								approvers={mr.approvers}
								reviewers={mr.reviewers}
								approvalsLeft={mr.approvalsLeft}
								subtle={true}
							/>
						</div>
					) : (
						<div className="flex shrink-0 flex-wrap items-center gap-2 rounded-xl bg-[var(--response-panel)] px-2 py-1.5 ring-1 ring-[var(--response-ring)] self-end lg:self-start">
							<CopyLinkButton
								mr={mr}
								className="inline-flex h-8 w-8 items-center justify-center !p-0 text-[var(--response-muted-text)] hover:bg-[var(--response-bubble)]"
							/>
							<span className="flex items-center gap-2 px-0.5">
								<MRStatusIcons mr={mr} iconClass="h-4 w-4" />
							</span>
							<span className="h-5 w-px bg-[var(--response-ring)]" />
							<span className="flex h-8 w-8 items-center justify-center">
								<PipelineStatus pipeline={mr.headPipeline} />
							</span>
							<span className="h-5 w-px bg-[var(--response-ring)]" />
							<ApprovalStatus
								aiReviewMissing={mr.aiReviewMissing}
								approvers={mr.approvers}
								reviewers={mr.reviewers}
								approvalsLeft={mr.approvalsLeft}
								subtle={true}
							/>
						</div>
					)}
				</div>
			</header>

			{isCardCollapsed ? (
				<button
					type="button"
					onClick={toggleCard}
					data-nav-item
					data-nav-toggle
					className={`flex w-full min-w-0 items-center gap-2 bg-[var(--response-panel)] text-left text-sm text-[var(--response-muted-text)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent)] ${
						compact ? "px-4 py-1.5" : "px-5 py-2.5 sm:px-6"
					}`}
				>
					<MessageCircle className="h-4 w-4 shrink-0 text-[var(--status-attention-text)]" />
					<span className="shrink-0 font-medium tabular-nums text-[var(--color-text-primary)]">
						{threadLabel}
					</span>
					<span className="min-w-0 truncate">
						· latest from {latestAuthor}: {previewOf(latest.body)}
					</span>
					<span className="ml-auto shrink-0 text-xs tabular-nums text-[var(--response-subtle-text)]">
						<DateTime date={latest.createdAt} />
					</span>
				</button>
			) : (
				<section
					id={threadsId}
					className={`bg-[var(--response-panel)] ${
						compact ? "px-4 py-2" : "px-5 py-3.5 sm:px-6"
					}`}
				>
					{!compact && (
						<div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
							<div className="flex items-center gap-2 text-sm font-medium text-[var(--color-text-primary)]">
								<span className="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-500/10 text-[var(--status-attention-text)]">
									<MessageCircle className="h-4 w-4" />
								</span>
								Awaiting your response
							</div>
							<span className="rounded-full bg-[var(--response-bubble)] px-2.5 py-1 text-xs font-medium tabular-nums text-[var(--response-muted-text)] ring-1 ring-[var(--response-ring)]">
								{threadLabel}
							</span>
						</div>
					)}
					<div className="min-w-0 space-y-1">
						{threads.map((thread, index) => {
							const key = threadKey(thread);
							return (
								<ResponseThread
									key={thread.id}
									thread={thread}
									position={index + 1}
									project={project}
									collapsed={!!collapsed[key]}
									onToggle={() => setKeys([key], !collapsed[key])}
									compact={compact}
								/>
							);
						})}
					</div>
				</section>
			)}
		</article>
	);
};

const ReviewerFilter = ({ reviewers, selected, onSelect }) => (
	<nav
		aria-label="Filter by reviewer"
		className="flex flex-wrap items-center gap-1.5 px-1"
	>
		<span className="mr-1 text-xs font-medium text-[var(--response-muted-text)]">
			From
		</span>
		{reviewers.map(({ author, count }) => {
			const active = selected === author.username;
			const name = author.name || author.username;
			return (
				// Links, not buttons: each chip is a ?from= URL, so it can be opened
				// in a new tab or copied. A plain click filters in place.
				<a
					key={author.username}
					href={hrefWithParam("from", active ? null : author.username)}
					onClick={(event) => {
						// Leave modified, non-primary and already-handled clicks to
						// the browser (new tab, new window, download).
						if (
							event.defaultPrevented ||
							event.button !== 0 ||
							event.metaKey ||
							event.ctrlKey ||
							event.shiftKey ||
							event.altKey
						)
							return;
						event.preventDefault();
						onSelect(active ? null : author.username);
					}}
					aria-current={active ? "true" : undefined}
					className={`inline-flex h-7 items-center gap-1.5 rounded-full pl-1 pr-2.5 text-xs font-medium ring-1 active:scale-[0.96] transition-[background-color,color,transform] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] ${
						active
							? "bg-[var(--response-bubble)] text-[var(--color-text-primary)] ring-[var(--color-accent)]"
							: "bg-[var(--response-card)] text-[var(--response-muted-text)] ring-[var(--response-ring)] hover:text-[var(--color-text-primary)]"
					}`}
				>
					<Avatar src={withAvatarBase(author.avatarUrl)} alt="" size="sm" />
					{name.split(" ")[0]}
					<span className="tabular-nums text-[var(--response-subtle-text)]">
						{count}
					</span>
				</a>
			);
		})}
	</nav>
);

const toolbarButton =
	"inline-flex h-11 items-center gap-1.5 rounded-xl bg-[var(--response-card)] px-3 text-sm font-medium shadow-sm ring-1 ring-[var(--response-ring)] hover:text-[var(--color-text-primary)] active:scale-[0.96] transition-transform";

export const CommentAttentionView = ({
	items: allItems,
	loading,
	pending = 0,
	unchecked = 0,
	error,
	onRetry,
}) => {
	const [showMarkdown, setShowMarkdown] = useState(false);
	const [collapsed, setKeys] = useCollapsedState();
	const [density, setDensity] = useLocalStorage(
		"needsResponseDensity",
		"comfortable",
	);
	const compact = density === "compact";
	// In the URL (?from=username) so a filtered inbox can be bookmarked.
	const [selectedReviewer, setSelectedReviewer] = useUrlParam("from");
	// Single-key shortcuts can be switched off (WCAG 2.1.4).
	const [shortcutsEnabled, setShortcutsEnabled] = useLocalStorage(
		"needsResponseShortcuts",
		true,
	);
	const [showShortcuts, setShowShortcuts] = useState(false);
	const listRef = useRef(null);

	const reviewers = useMemo(() => collectReviewers(allItems), [allItems]);
	// A reviewer whose last thread was just answered drops out of the list;
	// fall back to everyone rather than an empty inbox.
	const reviewer = reviewers.some((r) => r.author.username === selectedReviewer)
		? selectedReviewer
		: null;
	const items = useMemo(
		() => filterByReviewer(allItems, reviewer),
		[allItems, reviewer],
	);

	// "Collapse all" folds cards only, so expanding one shows its threads as
	// they were; it reads as "Expand all" once every card is folded.
	const cardKeys = items.map(cardKey);
	const allCollapsed =
		cardKeys.length > 0 && cardKeys.every((key) => collapsed[key]);
	const [copied, setCopied] = useState(false);
	const threadCount = items.reduce(
		(total, mr) => total + mr.commentAttention.count,
		0,
	);
	const [copyError, setCopyError] = useState(false);
	// Only rebuild the agent markdown when the markdown pane is shown (or being
	// copied); it concatenates every thread body and scales with inbox size.
	const agentMarkdown = useMemo(
		() => (showMarkdown ? buildAgentMarkdown(items) : ""),
		[showMarkdown, items],
	);
	const copyMarkdown = async () => {
		try {
			await navigator.clipboard.writeText(buildAgentMarkdown(items));
			setCopyError(false);
			setCopied(true);
			setTimeout(() => setCopied(false), 1600);
		} catch {
			setCopyError(true);
			setTimeout(() => setCopyError(false), 2600);
		}
	};

	const toggleShortcuts = useMemo(
		() => () => setShowShortcuts((value) => !value),
		[],
	);
	useInboxKeyboard(
		listRef,
		shortcutsEnabled && !showMarkdown && !loading,
		toggleShortcuts,
	);

	if (loading) {
		return (
			<output
				className="response-inbox block space-y-4"
				aria-busy="true"
				aria-label="Loading comments needing your response"
			>
				<div className="h-20 w-full max-w-xl rounded-xl bg-[var(--response-card)] animate-pulse" />
				{[1, 2, 3].map((item) => (
					<div
						key={item}
						className="h-56 rounded-2xl bg-[var(--response-card)] animate-pulse"
					/>
				))}
			</output>
		);
	}

	return (
		// Status text uses the --status-*-text tokens (index.css), which are set
		// for every dark palette; Tailwind dark: variants only match plain Dark.
		<div
			className={`response-inbox min-w-0 pb-10 ${compact ? "space-y-3" : "space-y-5"}`}
		>
			<header className="flex flex-col gap-3 px-1 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex min-w-0 items-start gap-3">
					<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-500/10 text-[var(--status-attention-text)] ring-1 ring-orange-500/15">
						<Inbox className="h-4 w-4" />
					</div>
					<div className="min-w-0">
						<p className="mb-0.5 text-xs font-medium text-[var(--status-attention-text)]">
							Response inbox
						</p>
						<h1 className="text-lg font-semibold leading-tight text-[var(--color-text-primary)] [text-wrap:balance]">
							Comments needing your response
						</h1>
						<p className="mt-0.5 text-xs text-[var(--response-muted-text)] [text-wrap:pretty]">
							Open merge requests ordered by the latest unanswered human
							activity.
						</p>
						{pending > 0 && (
							<p
								className="mt-1 inline-flex items-center gap-1.5 text-xs text-[var(--response-muted-text)]"
								aria-live="polite"
							>
								<Loader2 className="h-3 w-3 animate-spin" />
								Checking {pending} more {pending === 1 ? "MR" : "MRs"}
							</p>
						)}
					</div>
				</div>
				{!error && allItems.length > 0 && (
					<div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
						{!showMarkdown && (
							<>
								<button
									type="button"
									onClick={() => setShowShortcuts((value) => !value)}
									aria-pressed={showShortcuts}
									aria-label="Keyboard shortcuts"
									title="Keyboard shortcuts (?)"
									className={`${toolbarButton} w-11 justify-center !px-0 text-[var(--response-muted-text)]`}
								>
									<Keyboard className="h-4 w-4" />
								</button>
								<button
									type="button"
									onClick={() =>
										setDensity(compact ? "comfortable" : "compact")
									}
									aria-pressed={compact}
									title={compact ? "Comfortable layout" : "Compact layout"}
									className={`${toolbarButton} text-[var(--response-muted-text)]`}
								>
									{compact ? (
										<Rows2 className="h-4 w-4" />
									) : (
										<Rows4 className="h-4 w-4" />
									)}
									{compact ? "Comfortable" : "Compact"}
								</button>
								<button
									type="button"
									onClick={() => setKeys(cardKeys, !allCollapsed)}
									className={`${toolbarButton} text-[var(--response-muted-text)]`}
								>
									{allCollapsed ? (
										<ChevronsUpDown className="h-4 w-4" />
									) : (
										<ChevronsDownUp className="h-4 w-4" />
									)}
									{allCollapsed ? "Expand all" : "Collapse all"}
								</button>
							</>
						)}
						<div className="flex items-center rounded-xl bg-[var(--response-card)] p-1 shadow-sm ring-1 ring-[var(--response-ring)]">
							<button
								type="button"
								onClick={() => setShowMarkdown(false)}
								className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium active:scale-[0.96] transition-transform ${
									!showMarkdown
										? "bg-[var(--response-panel)] text-[var(--color-text-primary)] shadow-sm"
										: "text-[var(--response-muted-text)] hover:text-[var(--color-text-primary)]"
								}`}
								aria-pressed={!showMarkdown}
							>
								<LayoutList className="h-4 w-4" />
								Inbox
							</button>
							<button
								type="button"
								onClick={() => setShowMarkdown(true)}
								className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium active:scale-[0.96] transition-transform ${
									showMarkdown
										? "bg-[var(--response-panel)] text-[var(--color-text-primary)] shadow-sm"
										: "text-[var(--response-muted-text)] hover:text-[var(--color-text-primary)]"
								}`}
								aria-pressed={showMarkdown}
							>
								<FileText className="h-4 w-4" />
								Markdown
							</button>
						</div>
						<div className="flex items-center gap-3 rounded-xl bg-[var(--response-card)] px-3.5 py-2.5 shadow-sm ring-1 ring-[var(--response-ring)]">
							<div>
								<div className="text-base font-semibold tabular-nums text-[var(--color-text-primary)]">
									{items.length}
								</div>
								<div className="text-xs text-[var(--response-subtle-text)]">
									{items.length === 1 ? "MR" : "MRs"}
								</div>
							</div>
							<div className="h-8 w-px bg-[var(--response-ring)]" />
							<div>
								<div className="text-base font-semibold tabular-nums text-[var(--color-text-primary)]">
									{threadCount}
								</div>
								<div className="text-xs text-[var(--response-subtle-text)]">
									{threadCount === 1 ? "Thread" : "Threads"}
								</div>
							</div>
						</div>
					</div>
				)}
			</header>

			{showShortcuts && !showMarkdown && (
				<div className="space-y-3 rounded-xl bg-[var(--response-card)] px-4 py-3 text-xs shadow-sm ring-1 ring-[var(--response-ring)]">
					<label className="flex items-center gap-2 font-medium text-[var(--color-text-primary)]">
						<input
							type="checkbox"
							checked={shortcutsEnabled}
							onChange={(event) => setShortcutsEnabled(event.target.checked)}
							className="h-4 w-4 accent-[var(--color-accent)]"
						/>
						Single-key shortcuts
					</label>
					<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 sm:grid-cols-[auto_1fr_auto_1fr]">
						{KEYBOARD_SHORTCUTS.map(([keys, action]) => (
							<React.Fragment key={keys}>
								<dt>
									<kbd className="rounded bg-[var(--response-panel)] px-1.5 py-0.5 font-sans font-medium text-[var(--color-text-primary)] ring-1 ring-[var(--response-ring)]">
										{keys}
									</kbd>
								</dt>
								<dd className="text-[var(--response-muted-text)]">{action}</dd>
							</React.Fragment>
						))}
					</dl>
				</div>
			)}

			{!error && reviewers.length > 1 && (
				<ReviewerFilter
					reviewers={reviewers}
					selected={reviewer}
					onSelect={setSelectedReviewer}
				/>
			)}

			{error && (
				<div className="rounded-xl bg-[var(--color-error-bg)] px-4 py-3 text-sm text-[var(--color-error-text)] ring-1 ring-[var(--color-error-border)]">
					{error}
				</div>
			)}

			{!error && unchecked > 0 && pending === 0 && (
				<output className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[var(--color-error-bg)] px-4 py-3 text-sm text-[var(--color-error-text)] ring-1 ring-[var(--color-error-border)]">
					<span>
						Could not check {unchecked} merge{" "}
						{unchecked === 1 ? "request" : "requests"}, so this list may be
						incomplete. They are retried on the next refresh.
					</span>
					{onRetry && (
						<button
							type="button"
							onClick={onRetry}
							className="rounded-lg px-3 py-1.5 font-medium ring-1 ring-[var(--color-error-border)] hover:bg-[var(--color-error-border)] active:scale-[0.96] transition-transform"
						>
							Retry
						</button>
					)}
				</output>
			)}

			{!error && allItems.length === 0 && pending === 0 && unchecked === 0 && (
				<div className="flex min-h-64 flex-col items-center justify-center rounded-2xl bg-[var(--response-card)] px-6 py-12 text-center shadow-sm ring-1 ring-[var(--response-ring)]">
					<div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/10 text-[var(--status-success-text)]">
						<MessageCircle className="h-5 w-5" />
					</div>
					<h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
						You are all caught up
					</h2>
					<p className="mt-1 max-w-md text-sm text-[var(--response-muted-text)] [text-wrap:pretty]">
						No human comments are waiting for your response.
					</p>
				</div>
			)}

			{!error &&
				items.length > 0 &&
				(showMarkdown ? (
					<section className="overflow-hidden rounded-2xl bg-[var(--response-card)] shadow-[0_1px_2px_rgb(0_0_0/0.06),0_8px_28px_rgb(0_0_0/0.06)] ring-1 ring-[var(--response-ring)]">
						<div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 sm:px-6">
							<div>
								<h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
									Agent-ready Markdown
								</h2>
								<p className="mt-0.5 text-xs text-[var(--response-muted-text)]">
									Includes direct links and the full text of every pending human
									thread.
								</p>
							</div>
							<div className="flex flex-col items-end gap-1">
								<button
									type="button"
									onClick={copyMarkdown}
									className="inline-flex h-10 items-center gap-2 rounded-lg bg-[var(--color-accent)] px-3.5 text-sm font-medium text-[var(--color-accent-contrast)] hover:opacity-90 active:scale-[0.96] transition-transform"
								>
									{copied ? (
										<Check className="h-4 w-4" />
									) : (
										<Copy className="h-4 w-4" />
									)}
									{copied ? "Copied" : "Copy Markdown"}
								</button>
								{copyError && (
									<span className="text-xs font-medium text-red-500">
										Copy failed &mdash; select the text below to copy manually.
									</span>
								)}
							</div>
						</div>
						<pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap break-words border-t border-[var(--response-ring)] bg-[var(--response-panel)] px-5 py-5 font-mono text-xs leading-relaxed text-[var(--color-text-primary)] sm:px-6">
							{agentMarkdown}
						</pre>
					</section>
				) : (
					<div ref={listRef} className={compact ? "space-y-2" : "space-y-4"}>
						{items.map((mr) => (
							<ResponseCard
								key={mr.id}
								mr={mr}
								collapsed={collapsed}
								setKeys={setKeys}
								compact={compact}
							/>
						))}
					</div>
				))}
		</div>
	);
};
